import {
  BoardType,
  NotationType,
  EditMode,
  BusEventType,
} from "./unions";
import { UserAttributes } from "./userTypes";

export type EntityAttributes = {
  id?: number;
  uuid: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export type NotationAttributes =
  EntityAttributes & {
    parentUUId: string;
    user: UserAttributes;
    notationType: NotationType;
    boardType: BoardType;
    selected?: boolean;
    color?: ColorAttributes | null;
  };

export type ExponentAttributes = {
  value: string;
};

export type NotationCreationAttributes = Omit<
  NotationAttributes,
  "uuid"
>;

export type SingleValueAttributes = {
  value: string;
};

export type ColorAttributes = {
  id?: number;
  value: string;
};

export type DotCoordinates = {
  x: number;
  y: number;
};

export type AnnotationAttributes = {
  rotation: number;
};

export type ImageAttributes = {
  rotation?: number;
};

export type LineCoordinates = {
  top: DotCoordinates;
  bottom: DotCoordinates;
};

export type RectCoordinates = {
  topLeft: DotCoordinates;
  bottomRight: DotCoordinates;
};

export type CellAttributes = {
  col: number;
  row: number;
};

export type SelectedCell = CellAttributes & {
  userUUId: string;
  lessonUUId: number;
};

export type ColorizedCell = CellAttributes & {
  userUUId: string;
  lessonUUId: number;
  color: string;
};

export type LineAttributes = {
  p1x: number; // left
  p2x: number; // right
  p1y: number; // y which corresponds to x1, if the slope is positive this is the bottom(and higher) y and vice versa
  p2y: number; // y which corresponds to x2, if the slope is positive this is the top (and lower y) and vice versa
  dashed: boolean;
  arrowLeft: boolean;
  arrowRight: boolean;
};

export type CurveAttributes = {
  p1x: number; // left
  p2x: number; // right
  p1y: number; // y which corresponds to p1x
  p2y: number; // y which corresponds to p2x
  cpx: number; // control point x
  cpy: number; // control point y
};

export type FreeSketchAttributes = {
  points: DotCoordinates[];
};

export type CircleAttributes = {
  cx: number; // center x
  cy: number; // center y
  r: number; // radius
};

export type ConicKind = "parabola" | "hyperbola";
export type ConicAxis = "vertical" | "horizontal";

export type ConicAttributes = {
  kind: ConicKind;
  hx: number; // vertex (parabola) or center (hyperbola)
  hy: number;
  axis: ConicAxis;
  a: number; // pixel-space curvature / vertex scale; sign is opening / branch side
  b?: number; // parabola: drawn half-width (size). hyperbola: conjugate scale (opening)
  /** Points the parabola was fit through; dragging one refits. */
  through?: DotCoordinates[];
};

export type RectAttributes = {
  fromCol: number;
  toCol: number;
  fromRow: number;
  toRow: number;
};

export type MultiCellAttributes = {
  fromCol: number;
  toCol: number;
  row: number;
};

export type BoardAttributes = {
  name: string;
  user: UserAttributes;
};

export type Board = {
  uuid: string;
  type: BoardType;
};

export type PointNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    CellAttributes &
    SingleValueAttributes;

export type LineNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    LineAttributes;

export type CurveNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    CurveAttributes;

export type CircleNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    CircleAttributes;

export type ConicNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    ConicAttributes;

export type FreeSketchNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    FreeSketchAttributes;

export type RectNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    RectAttributes &
    SingleValueAttributes;

export type ImageNotationAttributes = RectNotationAttributes &
  ImageAttributes;

export type AnnotationNotationAttributes =
  AnnotationAttributes &
    EntityAttributes &
    NotationAttributes &
    DotCoordinates &
    SingleValueAttributes;

export type SqrtNotationAttributes =
  EntityAttributes &
    NotationAttributes &
    MultiCellAttributes;

// ommiting uuid from creation attributed since created by the databse
export type PointNotationCreationAttributes =
  Omit<
    NotationAttributes &
      CellAttributes &
      SingleValueAttributes,
    "uuid"
  >;

export type SqrtNotationCreationAttributes = Omit<
  SqrtNotationAttributes,
  "uuid"
>;

export type LineNotationCreationAttributes = Omit<
  NotationAttributes & LineAttributes,
  "uuid"
>;
export type RectNotationCreationAttributes = Omit<
  NotationAttributes &
    RectAttributes &
    SingleValueAttributes,
  "uuid"
>;

export type ImageNotationCreationAttributes = Omit<
  ImageNotationAttributes,
  "uuid"
>;
export type CurveNotationCreationAttributes =
  Omit<
    NotationAttributes & CurveAttributes,
    "uuid"
  >;

export type CircleNotationCreationAttributes =
  Omit<
    NotationAttributes & CircleAttributes,
    "uuid"
  >;

export type ConicNotationCreationAttributes =
  Omit<
    NotationAttributes & ConicAttributes,
    "uuid"
  >;

export type FreeSketchNotationCreationAttributes =
  Omit<
    NotationAttributes & FreeSketchAttributes,
    "uuid"
  >;

export type ExponentNotationCreationAttributes =
  Omit<
    EntityAttributes &
      NotationAttributes &
      CellAttributes &
      ExponentAttributes,
    "uuid"
  >;
export type AnnotationNotationCreationAttributes =
  Omit<
    EntityAttributes &
      NotationAttributes &
      DotCoordinates &
      SingleValueAttributes,
    "uuid"
  >;
export type ColorCreationAttributes = Omit<
  ColorAttributes,
  "id"
>;

export function isRect(n: NotationType): boolean {
  return n === "TEXT" || n === "IMAGE";
}

export function isLine(n: NotationType): boolean {
  return (
    n === "SQRT" ||
    n === "LINE" ||
    n === "DIVISIONLINE"
  );
}

export function isCurve(
  n: NotationType,
): boolean {
  return n === "CURVE";
}

export function isConic(
  n: NotationType,
): boolean {
  return n === "CONIC";
}

export function isCellNotationType(
  n: NotationType,
): boolean {
  return (
    n === "SQRTSYMBOL" ||
    n === "EXPONENT" ||
    n === "SYMBOL" ||
    n === "LOGBASE"
  );
}

export function isMultiCell(
  n: NotationType,
): boolean {
  return (
    n ===
    "SQRT" /*unlike horizontal line, sqrt borders adjusts to cell borders */
  );
}

export type MovementDirection =
  | "UP"
  | "DOWN"
  | "NONE";

export type SlopeType =
  | "POSITIVE"
  | "NEGATIVE"
  | "NONE";

export type lineWatcherEntry = {
  func: (p: DotCoordinates) => void;
  editMode: Array<EditMode>;
};

export type lineSaveWatcherEntry = {
  func: () => Promise<string>;
  editMode: Array<EditMode>;
};

export type lineMoveWatcherEntry = {
  func: (moveX: number, moveY: number) => void;
  editMode: Array<EditMode>;
};

export type lineSelectWatcherEntry = {
  func: (notation: NotationAttributes) => void;
  editMode: Array<EditMode>;
  event: BusEventType;
};

export type lineEndSelectionWatcherEntry = {
  editMode: Array<EditMode>;
};
